import React from 'react';

import { Box, FormHelperText, FormLabel, Stack, TextField } from '@mui/material';

import AIGeneratorButton from '~/components/AIGenerator/AIGenerateButton';
import MarkdownEditor from '~/components/Common/MarkdownEditor';

import type { OfferBuilderRoom } from '../types';

interface Props {
  onChange: (nextRoomValues: Partial<OfferBuilderRoom>) => void;
  propertyName: string;
  room: OfferBuilderRoom;
}

export default function DetailsTab({ onChange, propertyName, room }: Props) {
  const isDescriptionMissing = !room.description?.trim();
  const description = room.description?.trim() ? room.description : undefined;
  const amenities = room.amenities
    ?.map((amenity) => (typeof amenity === 'string' ? amenity : amenity.name))
    .filter((amenity): amenity is string => !!amenity);
  const aiContext = {
    model: 'room-type',
    field: 'description',
    propertyName,
    roomName: room.name || '',
    amenities,
  };

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 2,
        }}
      >
        <Stack spacing={1}>
          <FormLabel required>Name</FormLabel>
          <TextField
            value={room.name || ''}
            size="small"
            fullWidth
            required
            error={!room.name?.trim()}
            helperText={!room.name?.trim() ? 'Name is required' : undefined}
            slotProps={{ formHelperText: { sx: { ml: 0 } } }}
            onChange={(event) => onChange({ name: event.target.value })}
          />
        </Stack>
      </Box>

      <Stack spacing={1}>
        <FormLabel required>Description</FormLabel>
        <Box
          sx={{
            '& .rdw-editor-main': { minHeight: 180 },
            '& > div > .MuiBox-root': {
              borderColor: isDescriptionMissing ? 'error.main' : 'divider',
            },
          }}
        >
          <MarkdownEditor
            value={room.description || ''}
            placeholder="Describe the room, its size, view, bedding, and what makes it distinctive."
            wrapperId={`offer-builder-room-description-${room.id || room.name || 'room'}`}
            onBlur={(description) => onChange({ description })}
          />
        </Box>
        {isDescriptionMissing && <FormHelperText error>Description is required</FormHelperText>}
        <AIGeneratorButton
          context={aiContext}
          content={description}
          buttonLabel={description ? 'Rewrite with AI' : 'Draft with AI'}
          updateContext={() => aiContext}
          onChoose={(description) => onChange({ description })}
        />
      </Stack>
    </Stack>
  );
}
